import React, { useState } from "react";
import { useLocation, useNavigate, Navigate, useHref } from "react-router-dom";
import { handleRedirectBack } from "../utils/navigationUtils";
import "./ErrorPage.css";
import { logger } from "../utils/logger.client";
import { useTranslation } from "react-i18next";

/** Helper to detect "missing return url" messages (case/spacing tolerant) */
const isMissingReturnUrl = (msg: unknown): boolean => {
  const s = String(msg ?? "").toLowerCase().replace(/\s+/g, " ").trim();
  return (
      /missing.*return\s*url/.test(s) ||
      /return\s*url.*missing/.test(s) ||
      s.includes("missing returnurl") ||
      s.includes("missing return url")
  );
};

/** Inline redirect modal (same look as Login) */
const overlayStyles: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};
const modalStyles: React.CSSProperties = {
  background: "#fff",
  padding: "24px 28px",
  borderRadius: 12,
  minWidth: 320,
  textAlign: "center",
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
};
const spinnerStyles: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "4px solid #eee",
  borderTop: "4px solid #1976d2",
  animation: "spin 0.9s linear infinite",
  margin: "0 auto",
};
const InlineRedirectModal: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
    <div style={overlayStyles} role="dialog" aria-modal="true" aria-live="assertive">
      <div style={modalStyles}>
        <div style={spinnerStyles} aria-hidden />
        <h2 style={{ marginTop: 12, fontSize: 18, fontWeight: 700 }}>{title}</h2>
        <p style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
          {subtitle}
        </p>
      </div>
    </div>
);

const ErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hrefLogin = useHref("/login"); // basename-aware href for hard fallback
  const { t } = useTranslation();

  const state = (location.state as any) || {};

  const errorCode = state.errorCode || "500";
  const errorMessage =
    state.errorMessageKey
      ? t(state.errorMessageKey, state.errorMessageParams || {})
      : state.errorMessage || t("error.default.message");

  const [redirecting, setRedirecting] = useState(false);
  const missingReturnUrl =
    state.errorMessageKey === "error.missingReturnUrl" ||
    isMissingReturnUrl(errorMessage);

  const handleBackToPortal = async () => {
    if (redirecting) return;
    setRedirecting(true);

    try {
      if (missingReturnUrl) {
        // Prefer SPA navigate (keeps basename, clears history entry)
        navigate("/login", { replace: true, state: { fromError: true } });

        // Safety net: if we're still on /error soon after, hard reload
        window.setTimeout(() => {
          if (!/(^|\/)login(\/|$)/.test(window.location.pathname)) {
            window.location.replace(hrefLogin);
          }
        }, 700);

        return; // keep the "redirecting" overlay up
      }

      // We do have a returnUrl → delegate to util
      await handleRedirectBack(navigate, true);
      setRedirecting(false);
    } catch (e) {
      logger.error("[ErrorPage] Error during handleBackToPortal:", e);
      setRedirecting(false);
    }
  };


  return (
      <div className="error-wrapper container py-5" id="BOX_ERROR_PAGE_CONTAINER">
        <div className="error-card" id="BOX_ERROR_CARD">
          <div className="error-header" role="alert" aria-live="polite">
          <span className="error-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
              <path d="M12 2 1 21h22L12 2zm0 6c.55 0 1 .45 1 1v6a1 1 0 0 1-2 0V9c0-.55.45-1 1-1zm0 10a1.25 1.25 0 1 0 0 2.5A1.25 1.25 0 0 0 12 18z"/>
            </svg>
          </span>
            <h2 className="error-title" id="TXT_ERROR_TITLE">{t("error.title")}</h2>
          </div>

          <div className="error-body">
            <div className="error-table-wrap" id="BOX_ERROR_TABLE_CONTAINER">
              <table className="error-table" id="TABLE_ERROR_DETAILS" aria-describedby="TXT_ERROR_TITLE">
                <thead>
                <tr>
                  <th scope="col">{t("error.table.field")}</th>
                  <th scope="col">{t("error.table.description")}</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td className="key-cell"><strong>{t("error.table.errorCode")}</strong></td>
                  <td className="value-cell" id="TXT_ERROR_CODE_VALUE">{errorCode}</td>
                </tr>
                <tr>
                  <td className="key-cell"><strong>{t("error.table.message")}</strong></td>
                  <td className="value-cell" id="TXT_ERROR_MESSAGE_VALUE">{errorMessage}</td>
                </tr>
                </tbody>
              </table>
            </div>

            <div className="button-container" id="BOX_ERROR_BUTTONS">
              {!redirecting && (
                  <button
                      id="BTN_CANCEL"
                      type="button"
                      className="gov-btn"
                      onClick={handleBackToPortal}
                  >
                    {t("error.button.back")}
                  </button>
              )}
            </div>

            {redirecting && (
                <div className="redirecting" id="BOX_REDIRECTING_SPINNER">
                  <InlineRedirectModal
                    title={
                      missingReturnUrl
                        ? t("error.redirect.title.login")
                        : t("error.redirect.title.portal")
                    }
                    subtitle={t("error.redirect.subtitle")}
                  />
                  {/* Hidden text kept for Selenium assertions if needed */}
                  <p
                      id="TXT_REDIRECTING"
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
                      aria-live="polite"
                  >
                    {missingReturnUrl
                      ? t("error.redirect.hidden.login")
                      : t("error.redirect.hidden.portal")}
                  </p>

                  {missingReturnUrl && <Navigate to="/login" replace state={{ fromError: true }} />}
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default ErrorPage;
